/**
 * V2 Options List Section Configuration
 *
 * Maps V2 StepNode.content.options paths for list/grid screen types.
 *
 * V2 OptionsList:
 * - selectionMode: 'browse' | 'one' | 'many'
 * - columnLayout: 'oneCol' | 'twoCols'
 * - itemLayout: ItemLayout enum
 * - items: Array<OptionsItem>
 * - appearance: OptionsListStyle
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import { createV2OptionsListStyleSections } from './v2OptionsListStyleConfig';
import { getValueByPath } from '../utils/v2FieldOperations';

/** Cell height as percentage of cell width (100 = square, 120 = taller) */
export const V2_CELL_HEIGHT_CONFIG: FieldConfig = {
  path: 'content.options.cellHeightFromCellWidth',
  label: 'Cell Height (% of width)',
  type: 'number',
  min: 30,
  max: 300,
  step: 5,
  defaultValue: 100,
  isNullable: true,
};

export const V2_SELECTION_MODE_OPTIONS = [
  { value: 'browse', label: 'Browse (no selection)' },
  { value: 'one', label: 'Single Select' },
  { value: 'many', label: 'Multi Select' },
];

export const V2_COLUMN_LAYOUT_OPTIONS = [
  { value: 'oneCol', label: 'One Column' },
  { value: 'twoCols', label: 'Two Columns' },
];

/**
 * Fallback ItemLayout options (used when screen type config doesn't specify validItemLayouts).
 * Values must match V2 ItemLayout enum exactly.
 */
export const V2_ITEM_LAYOUT_OPTIONS = [
  { value: 'Title', label: 'Title' },
  { value: 'TitleSubtitle', label: 'Title + Subtitle' },
  { value: 'ImageTitle', label: 'Image + Title' },
  { value: 'ImageTitleSubtitle', label: 'Image + Title + Subtitle' },
];

/**
 * Human-readable labels for all 18 ItemLayout values.
 */
export const ITEM_LAYOUT_LABELS: Record<string, string> = {
  // Base oneCol layouts
  ImageTitle: 'Image + Title',
  TitleImage: 'Title + Image',
  ImageTitleSubtitle: 'Image + Title + Subtitle',
  TitleSubtitleImage: 'Title + Subtitle + Image',
  TitleSubtitle: 'Title + Subtitle',
  Title: 'Title',
  // Checkbox oneCol layouts
  TitleSubtitleCheckbox: 'Title + Subtitle + Checkbox',
  CheckboxTitleSubtitle: 'Checkbox + Title + Subtitle',
  ImageTitleSubtitleCheckbox: 'Image + Title + Subtitle + Checkbox',
  CheckboxTitleSubtitleImage: 'Checkbox + Title + Subtitle + Image',
  ImageTitleCheckbox: 'Image + Title + Checkbox',
  CheckboxTitleImage: 'Checkbox + Title + Image',
  TitleCheckbox: 'Title + Checkbox',
  CheckboxTitle: 'Checkbox + Title',
  // Grid (twoCols) layouts
  SmallImageTitle: 'Small Image + Title',
  MediumImageTitle: 'Medium Image + Title',
  BigImageTitle: 'Big Image + Title',
  FullImage: 'Full Image',
};

/**
 * Build dropdown options from a list of valid layout values.
 */
export function buildItemLayoutOptions(validLayouts: string[]): Array<{ value: string; label: string }> {
  return validLayouts.map(value => ({
    value,
    label: ITEM_LAYOUT_LABELS[value] ?? value,
  }));
}

// ── Item layout resolution via screen type configs (single source of truth) ──

import { getV2ScreenTypeConfig } from './v2ScreenTypeConfig';

/**
 * Map (columnLayout, selectionMode) → matching screen type → validItemLayouts.
 *
 * Uses V2ScreenTypeConfig as the single source of truth:
 *   oneCol  + one    → ListSinglePick
 *   oneCol  + many   → ListMultiPick
 *   oneCol  + browse → HeroList
 *   twoCols + one    → GridSinglePick
 *   twoCols + many   → GridMultiPick
 *   twoCols + browse → GridSinglePick (grid browse uses same layouts as grid single)
 */
const CONTEXT_TO_SCREEN_TYPE: Record<string, Record<string, string>> = {
  oneCol: {
    one: 'ListSinglePick',
    many: 'ListMultiPick',
    browse: 'HeroList',
  },
  twoCols: {
    one: 'GridSinglePick',
    many: 'GridMultiPick',
    browse: 'GridSinglePick',
  },
};

/**
 * Get valid item layouts for a given columnLayout + selectionMode combination.
 * Resolves to the corresponding screen type config and returns its validItemLayouts.
 */
export function getValidItemLayoutsForContext(
  columnLayout: string,
  selectionMode: string,
): string[] {
  const screenType = CONTEXT_TO_SCREEN_TYPE[columnLayout]?.[selectionMode]
    ?? CONTEXT_TO_SCREEN_TYPE['oneCol']['one'];
  const config = getV2ScreenTypeConfig(screenType);
  return config.validItemLayouts ?? ['Title'];
}

/**
 * Determine which fields are visible for a given itemLayout.
 * Parses the layout name to determine flags rather than using a switch on all 18 variants.
 */
export function getV2OptionsItemVisibility(itemLayout: string) {
  const name = itemLayout || 'Title';
  const hasImage = name.includes('Image');
  const hasSubtitle = name.includes('Subtitle');
  const hasCheckbox = name.includes('Checkbox');

  return {
    showTitle: true,
    showSubtitle: hasSubtitle,
    showImage: hasImage,
    showCheckbox: hasCheckbox,
  };
}

/**
 * Create a new V2 options item with appearance/layout values read from
 * the style section configs. Reads current values from step's items.0
 * (the style sections' source of truth), falling back to field config defaults.
 *
 * @param step - Current step data (to read items.0 appearance values from)
 * @param locale - Current locale for translations
 * @param index - 1-based display index for the new item
 * @param variableValue - Optional semantic key for variable binding
 */
export function createV2OptionsItemFromSections(
  step: unknown,
  locale: string,
  index: number,
  variableValue?: string,
): Record<string, unknown> {
  const sections = createV2OptionsListStyleSections();
  const existingItems = getValueByPath<unknown[]>(step, 'content.options.items');
  const hasTemplateItem = Array.isArray(existingItems) && existingItems.length > 0;

  const item: Record<string, unknown> = {
    title: { translations: { [locale]: `Item ${index}` }, kind: 'static' },
    subtitle: { translations: { [locale]: '' }, kind: 'static' },
    action: { kind: 'advance', edges: [] },
    weight: index,
  };
  if (variableValue !== undefined) {
    item.variableValue = variableValue;
  }

  const ITEM_PREFIX = 'content.options.items.0.';

  // Apply values from style section fields that target items.0.*
  for (const section of sections) {
    if (!section.fields) continue;
    for (const field of section.fields) {
      if (!field.path.startsWith(ITEM_PREFIX)) continue;
      const relativePath = field.path.slice(ITEM_PREFIX.length); // e.g. 'title.appearance.fontSize'

      // Read current value from step (what the style section actually shows)
      const currentValue = getValueByPath(step, field.path);
      const value = currentValue !== undefined && currentValue !== null
        ? currentValue
        : hasTemplateItem
          ? undefined
          : field.defaultValue;
      if (value !== undefined) {
        setNestedOnItem(item, relativePath, value);
      }
    }

    // Also copy padding (layout.insets) from item-level padding sections
    if (section.paddingConfig?.basePath.startsWith(ITEM_PREFIX)) {
      const relBase = section.paddingConfig.basePath.slice(ITEM_PREFIX.length);
      const insetsValue = getValueByPath(step, section.paddingConfig.basePath);
      if (insetsValue && typeof insetsValue === 'object') {
        setNestedOnItem(item, relBase, JSON.parse(JSON.stringify(insetsValue)));
      }
    }
  }

  return item;
}

/** Set a nested value on a plain object by dot-path (e.g. 'title.appearance.fontSize') */
function setNestedOnItem(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  current[keys[keys.length - 1]] = value;
}

/**
 * Fallback: create minimal default item when no items exist to read from.
 */
export function createDefaultV2OptionsItem(locale: string, index: number, variableValue?: string) {
  const item: Record<string, unknown> = {
    title: {
      translations: { [locale]: `Item ${index}` },
      kind: 'static',
    },
    subtitle: {
      translations: { [locale]: '' },
      kind: 'static',
    },
    action: { kind: 'advance', edges: [] },
    weight: index,
  };
  if (variableValue !== undefined) {
    item.variableValue = variableValue;
  }
  return item;
}
